import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

const TypistCycle = ({
  content = [''],
  numberOfCycles = 1,
  segmentDelay = 0.75,
  typingDelay = 80,
  className = ''
}) => {
  const lines = useMemo(() => (content && content.length ? content : ['']), [content]);
  const [currentLine, setCurrentLine] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [phase, setPhase] = useState('typing');

  const infiniteLoop = numberOfCycles < 0;

  useEffect(() => {
    const targetText = lines[currentLine] || '';

    if (phase === 'typing') {
      if (displayedText.length < targetText.length) {
        const timeout = setTimeout(() => {
          setDisplayedText(targetText.slice(0, displayedText.length + 1));
        }, typingDelay);

        return () => clearTimeout(timeout);
      }

      setPhase('waiting');
    }

    if (phase === 'waiting') {
      const timeout = setTimeout(() => {
        const nextLine = (currentLine + 1) % lines.length;
        const loopCompleted = nextLine === 0;
        const completed = loopCompleted ? cyclesCompleted + 1 : cyclesCompleted;

        if (loopCompleted) {
          setCyclesCompleted(completed);
        }

        if (!infiniteLoop && loopCompleted && completed >= numberOfCycles) {
          setPhase('done');
          return;
        }

        setCurrentLine(nextLine);
        setDisplayedText('');
        setPhase('typing');
      }, segmentDelay * 1000);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [
    phase,
    displayedText,
    lines,
    currentLine,
    typingDelay,
    segmentDelay,
    cyclesCompleted,
    numberOfCycles,
    infiniteLoop
  ]);

  useEffect(() => {
    if (phase === 'done') {
      const targetText = lines[currentLine] || '';
      if (displayedText !== targetText) {
        setDisplayedText(targetText);
      }
    }
  }, [phase, displayedText, lines, currentLine]);

  return (
    <span className={`typist-cycle ${className || ''}`.trim()}>
      <span className="typist-cycle__text" aria-live="polite">
        {displayedText}
      </span>
      <span className="typist-cycle__cursor" aria-hidden="true">
        |
      </span>
    </span>
  );
};

TypistCycle.propTypes = {
  content: PropTypes.arrayOf(PropTypes.string),
  numberOfCycles: PropTypes.number,
  segmentDelay: PropTypes.number,
  typingDelay: PropTypes.number,
  className: PropTypes.string
};

export default TypistCycle;
